// ./src/components/layouts/DashboardLayout/BottomTabBar.tsx
// Fixed bottom tab navigation bar for mobile devices.
// Dynamically takes the first 4 visible navigation items as tabs
// and adds a "More" tab to reveal remaining items.

import React, { useMemo, useCallback } from "react";
import {
  Box,
  Group,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { SiteMapItem } from "@/config";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface BottomTabBarProps {
  /** Full list of visible navigation items from sitemap */
  navigationItems: SiteMapItem[];

  /** Current route path for active tab detection */
  currentPath: string;

  /** Navigation handler for tab clicks */
  onNavigate: (path: string) => void;

  /** Whether the More view is currently active */
  isMoreActive: boolean;

  /** Handler for More tab toggle */
  onMoreClick: () => void;

  /** Function to check if a nav item (or its children) is active */
  isItemActive: (item: SiteMapItem) => boolean;

  /** Translation helper for nav item names */
  getTranslatedName: (translationKey?: string, fallback?: string) => string;
}

// ============================================
// Constants
// ============================================

/** Height of the tab bar (excluding safe area) */
const TAB_BAR_HEIGHT = 64;

/** Extra bottom padding beyond safe-area to avoid iPhone home indicator conflicts */
const EXTRA_BOTTOM_PADDING = 20;

/** Default tab icons when sitemap item has no icon */
const DEFAULT_ICON = "Circle";

/** More tab icon */
const MORE_ICON = "DotsVertical";

// ============================================
// Tap animation styles
// ============================================

const TAB_TAP_STYLES = `
  .bottom-tab-btn {
    transition: transform 100ms ease, opacity 150ms ease;
  }
  .bottom-tab-btn:active {
    transform: scale(0.88);
    opacity: 0.7;
  }
`;

// ============================================
// Tab Button Sub-component
// ============================================

interface TabButtonProps {
  iconName: string;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  iconName,
  label,
  isActive,
  onClick,
}) => {
  const theme = useMantineTheme();

  const activeColor = theme.colors.brand[6];
  const inactiveColor = theme.colors.neutral[5];

  return (
    <UnstyledButton
      onClick={onClick}
      className="bottom-tab-btn"
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        paddingTop: 8,
        paddingBottom: 6,
        minWidth: 0,
        minHeight: 52,
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <AppIcon
        iconName={iconName}
        size={24}
        color={isActive ? activeColor : inactiveColor}
      />
      <Text
        size="11px"
        fw={isActive ? 600 : 500}
        c={isActive ? "brand.6" : "neutral.5"}
        ta="center"
        lh={1.2}
        truncate
        style={{ maxWidth: "100%" }}
      >
        {label}
      </Text>
    </UnstyledButton>
  );
};

// ============================================
// Main Component
// ============================================

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  navigationItems,
  //currentPath,
  onNavigate,
  isMoreActive,
  onMoreClick,
  isItemActive,
  getTranslatedName,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  // Split: first 4 as tabs, rest go under "More"
  const tabItems = useMemo(
    () => {
      const items = navigationItems.filter(item => Boolean(item.bottomNavBar));
      items.sort((a: any, b: any) => {
        return a.bottomNavBar - b.bottomNavBar;
      });

      return items;
    },
    [navigationItems]
  );

  const remainingItems = useMemo(
    () => {
      const items = navigationItems.filter(item => !Boolean(item.bottomNavBar));
      return items;
    },
    [navigationItems]
  );

  // Check if any remaining item is active (to highlight "More" tab)
  const isAnyRemainingActive = useMemo(() => {
    return remainingItems.some((item) => isItemActive(item));
  }, [remainingItems, isItemActive]);

  // Handle tab navigation — also closes More if open
  const handleTabClick = useCallback(
    (path: string) => {
      onNavigate(path);
    },
    [onNavigate]
  );

  return (
    <>
      {/* Inject tap animation styles */}
      <style>{TAB_TAP_STYLES}</style>

      <Box
        hiddenFrom="md"
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        style={{
          borderTop: `1px solid ${theme.colors.neutral[2]}`,
          zIndex: 100,
          paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${EXTRA_BOTTOM_PADDING}px)`,
        }}
      >
        <Group
          gap={0}
          wrap="nowrap"
          h={TAB_BAR_HEIGHT}
          align="stretch"
        >
          {/* Dynamic tabs from first 4 sitemap items */}
          {tabItems.map((item, index) => {
            const isActive = !isMoreActive && item.url
              ? isItemActive(item)
              : false;

            return (
              <TabButton
                key={item.url || `tab-${index}`}
                iconName={item.icon || DEFAULT_ICON}
                label={getTranslatedName(item.options?.translationKey, item.name)}
                isActive={isActive}
                onClick={() => item.url && handleTabClick(item.url)}
              />
            );
          })}

          {/* More tab */}
          <TabButton
            iconName={MORE_ICON}
            label={t("navigation.more", { defaultValue: "More" })}
            isActive={isMoreActive || isAnyRemainingActive}
            onClick={onMoreClick}
          />
        </Group>
      </Box>
    </>
  );
};